/**
 * Homestead piece collision — walkable floors + solid walls from placed pieces.
 *
 * Every catalog piece compiles to BOX primitives, so collision runs on
 * per-INSTANCE oriented boxes (OBBs): stairs collide as their four real steps
 * (climbable), doorway walls as flanks + header (the gap is walk-through), a
 * railing as its rail — not as whole-piece slabs. Non-box primitives (furniture
 * lamp poles etc.) are SKIPPED — decorative rounds don't collide.
 *
 * SHARED (moved from client/src/homestead/collision.ts, 2026-08-02) because the
 * settlement-loop work needs these queries on both sides: the client for
 * character/camera/bolt hooks, the server (and the P1 room solver) for
 * dwelling/shelter validation — the support.ts precedent exactly. Pure +
 * pmath-safe (arithmetic + Math.sqrt/abs/min/max only); compileAsset is already
 * pmath-routed. The client re-exports from its old path.
 *
 * Queries mirroring the settlement collider contracts character.ts consumes
 * (planet-LOCAL metres):
 *  - standRadiusObbs()  → highest walkable floor radius under a point (roofStand)
 *  - resolveObbs()      → lateral push-out to a corrected position (wall solver)
 *  - occludesObbs()     → sphere overlap (camera boom pull-in)
 *  - segmentHitObbs()   → first segment crossing (bolt impacts / shelter rays)
 */
import { compileAsset } from "../proceduralAssetCompile.js";
import { PIECE_CATALOG } from "./pieces.js";
import type { PlacedPiece, Quat, Vec3 } from "./types.js";

/** A face steeper than this vs the radial up is a wall, not a floor. */
const WALK_COS = 0.5;
/** Stand probe: cast from this far above the query point… */
const PROBE_UP = 0.5;
/** …downward this far (catches drops; clampToGround gates by fall speed). */
const PROBE_LEN = 5;
/** Push directions within this of ±up are vertical contacts — stand/ceiling
 *  territory, never the lateral solver's (else floors fight the ground clamp). */
const VERTICAL_COS = 0.72;
/** The lateral probe sits this far above the feet (belly height) — walls and
 *  rails intersect it; the floor plane underfoot stays out of reach. */
const BODY_LIFT = 0.85;
/** Ceiling probe: a small head sphere this far above the feet. Only DOWNWARD
 *  contacts apply (jumping into a floor from below bonks); floor-up contacts
 *  stay the ground clamp's. */
const HEAD_LIFT = 1.55;
const HEAD_RADIUS = 0.35;

/** One oriented box: centre, orientation, half-extents, bounding radius. */
export interface WorldObb {
  pid: number;
  c: Vec3;
  q: Quat;
  h: Vec3;
  r: number;
}

export interface LocalObb { c: Vec3; q: Quat; h: Vec3; r: number }

// ── quat helpers (plain arithmetic; allocation-light) ────────────────────────

function qMul(a: Quat, b: Quat): Quat {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] + a[1] * b[3] + a[2] * b[0] - a[0] * b[2],
    a[3] * b[2] + a[2] * b[3] + a[0] * b[1] - a[1] * b[0],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}
function qRot(q: Quat, v: Vec3): Vec3 {
  const [qx, qy, qz, qw] = q;
  const tx = 2 * (qy * v[2] - qz * v[1]);
  const ty = 2 * (qz * v[0] - qx * v[2]);
  const tz = 2 * (qx * v[1] - qy * v[0]);
  return [
    v[0] + qw * tx + qy * tz - qz * ty,
    v[1] + qw * ty + qz * tx - qx * tz,
    v[2] + qw * tz + qx * ty - qy * tx,
  ];
}
function qInvRot(q: Quat, v: Vec3): Vec3 {
  return qRot([-q[0], -q[1], -q[2], q[3]], v);
}

/** Rotation part of a column-major T·R matrix → quaternion (Shoemake). */
function matQuat(m: number[]): Quat {
  const m00 = m[0], m10 = m[1], m20 = m[2];
  const m01 = m[4], m11 = m[5], m21 = m[6];
  const m02 = m[8], m12 = m[9], m22 = m[10];
  const trace = m00 + m11 + m22;
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2;
    return [(m21 - m12) / s, (m02 - m20) / s, (m10 - m01) / s, s / 4];
  } else if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1 + m00 - m11 - m22) * 2;
    return [s / 4, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s];
  } else if (m11 > m22) {
    const s = Math.sqrt(1 + m11 - m00 - m22) * 2;
    return [(m01 + m10) / s, s / 4, (m12 + m21) / s, (m02 - m20) / s];
  }
  const s = Math.sqrt(1 + m22 - m00 - m11) * 2;
  return [(m02 + m20) / s, (m12 + m21) / s, s / 4, (m10 - m01) / s];
}

// ── catalog OBBs (doc-space, memoized per catalog index) ─────────────────────

let obbCache: (LocalObb[] | null)[] | null = null;

/** The collision boxes of catalog piece `t` in the piece's local frame.
 *  Compiled here (not via the client mesh cache) so the server shares it. */
export function catalogObbs(t: number): LocalObb[] {
  if (!obbCache) obbCache = PIECE_CATALOG.map(() => null);
  const cached = obbCache[t];
  if (cached) return cached;
  const entry = PIECE_CATALOG[t];
  const compiled = entry ? compileAsset(entry.doc) : undefined;
  const out: LocalObb[] = [];
  for (const inst of compiled?.instances ?? []) {
    if (inst.kind !== "box") continue; // box-only; rounds don't collide
    const h: Vec3 = [inst.params[0] / 2, inst.params[1] / 2, inst.params[2] / 2];
    out.push({
      c: [inst.transform[12], inst.transform[13], inst.transform[14]],
      q: matQuat(inst.transform),
      h,
      r: Math.sqrt(h[0] * h[0] + h[1] * h[1] + h[2] * h[2]),
    });
  }
  if (t >= 0 && t < (obbCache?.length ?? 0)) obbCache[t] = out;
  return out;
}

/** A placed piece's collision boxes in planet-local space. */
export function pieceWorldObbs(piece: PlacedPiece): WorldObb[] {
  return catalogObbs(piece.t).map((o) => {
    const c = qRot(piece.q, o.c);
    return {
      pid: piece.pid,
      c: [c[0] + piece.p[0], c[1] + piece.p[1], c[2] + piece.p[2]] as Vec3,
      q: qMul(piece.q, o.q),
      h: o.h,
      r: o.r,
    };
  });
}

// ── stand: radial ray down onto walkable faces ───────────────────────────────

/**
 * Highest walkable floor radius under planet-local point p (null = none).
 * Radial ray from PROBE_UP above p, downward PROBE_LEN; slab-intersects each
 * OBB; a hit face counts only when its normal is floor-like (·up ≥ WALK_COS) —
 * side faces belong to the lateral solver.
 */
export function standRadiusObbs(obbs: readonly WorldObb[], px: number, py: number, pz: number): number | null {
  const pr = Math.sqrt(px * px + py * py + pz * pz) || 1;
  const up: Vec3 = [px / pr, py / pr, pz / pr];
  const o: Vec3 = [px + up[0] * PROBE_UP, py + up[1] * PROBE_UP, pz + up[2] * PROBE_UP];
  const d: Vec3 = [-up[0], -up[1], -up[2]];
  let best: number | null = null;
  for (const b of obbs) {
    // Coarse prefilter: the ray segment must come near the box at all.
    const mx = b.c[0] - o[0], my = b.c[1] - o[1], mz = b.c[2] - o[2];
    const along = mx * d[0] + my * d[1] + mz * d[2];
    if (along < -b.r || along > PROBE_LEN + b.r) continue;
    const cx = mx - along * d[0], cy = my - along * d[1], cz = mz - along * d[2];
    if (cx * cx + cy * cy + cz * cz > b.r * b.r) continue;
    // Slab test in the box frame.
    const lo = qInvRot(b.q, [o[0] - b.c[0], o[1] - b.c[1], o[2] - b.c[2]]);
    const ld = qInvRot(b.q, d);
    let tMin = 0, tMax = PROBE_LEN;
    let axis = -1, sign = 0;
    let miss = false;
    for (let i = 0; i < 3; i++) {
      if (Math.abs(ld[i]) < 1e-9) {
        if (Math.abs(lo[i]) > b.h[i]) { miss = true; break; }
        continue;
      }
      let t1 = (-b.h[i] - lo[i]) / ld[i];
      let t2 = (b.h[i] - lo[i]) / ld[i];
      let s = -1; // entering through the -h face…
      if (t1 > t2) { const tt = t1; t1 = t2; t2 = tt; s = 1; } // …or the +h face
      if (t1 > tMin) { tMin = t1; axis = i; sign = s; }
      if (t2 < tMax) tMax = t2;
      if (tMin > tMax) { miss = true; break; }
    }
    if (miss || axis < 0 || tMin <= 0 || tMin > PROBE_LEN) continue;
    // Walkability: the entered face's world normal vs radial up.
    const n: Vec3 = [0, 0, 0];
    n[axis] = sign;
    const wn = qRot(b.q, n);
    if (wn[0] * up[0] + wn[1] * up[1] + wn[2] * up[2] < WALK_COS) continue;
    const radius = pr + PROBE_UP - tMin;
    if (best === null || radius > best) best = radius;
  }
  return best;
}

// ── occlusion: sphere-vs-OBB overlap (camera boom contract) ──────────────────

/**
 * True when a sphere at planet-local p overlaps ANY piece box — the camera
 * boom's pull-in query (mirrors BoxColliderField.occludes). Full-height and
 * orientation-exact: unlike resolve, no vertical-contact exemption — under a
 * placed floor/roof the boom SHOULD pull in.
 */
export function occludesObbs(obbs: readonly WorldObb[],
  px: number, py: number, pz: number, radius: number): boolean {
  for (const b of obbs) {
    const dx = b.c[0] - px, dy = b.c[1] - py, dz = b.c[2] - pz;
    const reach = b.r + radius;
    if (dx * dx + dy * dy + dz * dz > reach * reach) continue;
    const lp = qInvRot(b.q, [px - b.c[0], py - b.c[1], pz - b.c[2]]);
    const gx = lp[0] - Math.max(-b.h[0], Math.min(b.h[0], lp[0]));
    const gy = lp[1] - Math.max(-b.h[1], Math.min(b.h[1], lp[1]));
    const gz = lp[2] - Math.max(-b.h[2], Math.min(b.h[2], lp[2]));
    if (gx * gx + gy * gy + gz * gz < radius * radius) return true;
  }
  return false;
}

// ── segment: first crossing fraction (bolt contract) ─────────────────────────

/**
 * Earliest fraction t ∈ [0,1] where segment a→b enters ANY piece box, or null
 * (mirrors BoxColliderField.segmentHit — the bolt-impact query). Slab test in
 * each box's local frame; a segment STARTING inside a box reports t = 0.
 */
export function segmentHitObbs(obbs: readonly WorldObb[],
  ax: number, ay: number, az: number, bx: number, by: number, bz: number): number | null {
  const dx = bx - ax, dy = by - ay, dz = bz - az;
  const segLen = Math.sqrt(dx * dx + dy * dy + dz * dz);
  let best: number | null = null;
  for (const b of obbs) {
    // Coarse prefilter: segment-point distance vs bounding radius.
    const mx = b.c[0] - ax, my = b.c[1] - ay, mz = b.c[2] - az;
    const along = segLen > 1e-9 ? (mx * dx + my * dy + mz * dz) / (segLen * segLen) : 0;
    const t0 = Math.max(0, Math.min(1, along));
    const cx = mx - dx * t0, cy = my - dy * t0, cz = mz - dz * t0;
    if (cx * cx + cy * cy + cz * cz > b.r * b.r) continue;
    const lo = qInvRot(b.q, [ax - b.c[0], ay - b.c[1], az - b.c[2]]);
    const ld = qInvRot(b.q, [dx, dy, dz]);
    let tMin = 0, tMax = 1;
    let miss = false;
    for (let i = 0; i < 3; i++) {
      if (Math.abs(ld[i]) < 1e-9) {
        if (Math.abs(lo[i]) > b.h[i]) { miss = true; break; }
        continue;
      }
      let t1 = (-b.h[i] - lo[i]) / ld[i];
      let t2 = (b.h[i] - lo[i]) / ld[i];
      if (t1 > t2) { const tt = t1; t1 = t2; t2 = tt; }
      if (t1 > tMin) tMin = t1;
      if (t2 < tMax) tMax = t2;
      if (tMin > tMax) { miss = true; break; }
    }
    if (miss) continue;
    if (best === null || tMin < best) best = tMin;
  }
  return best;
}

// ── ray: TRUE entry distance (the crosshair-pick contract) ───────────────────

/** The box a ray entered first: which piece, and how far along (metres). */
export interface ObbRayHit { pid: number; dist: number }

/**
 * The FIRST box a UNIT ray enters, by true entry distance, or null.
 *
 * Unlike segmentHitObbs this returns METRES (not a fraction), reports the pid,
 * and takes a `pad` that inflates every box — the crosshair is a mathematical
 * line and a railing rail is 0.12 m thick, so an unpadded pick is unhittable in
 * practice. A ray ORIGINATING inside a box reports 0.
 *
 * This is the demolish pick's core: the true entry point, so the piece the
 * crosshair actually crosses FIRST wins — a bounding-sphere pick ranks by
 * centre proximity instead and hands you the piece next to the one you meant.
 */
export function rayHitObbs(obbs: readonly WorldObb[],
  ox: number, oy: number, oz: number, dx: number, dy: number, dz: number,
  maxDist: number, pad = 0): ObbRayHit | null {
  let best: ObbRayHit | null = null;
  for (const b of obbs) {
    // Coarse prefilter: ray-point distance vs (bounding radius + pad).
    const mx = b.c[0] - ox, my = b.c[1] - oy, mz = b.c[2] - oz;
    const along = mx * dx + my * dy + mz * dz;
    if (along - b.r - pad > maxDist) continue;
    const t0 = Math.max(0, along);
    const cx = mx - dx * t0, cy = my - dy * t0, cz = mz - dz * t0;
    const reach = b.r + pad;
    if (cx * cx + cy * cy + cz * cz > reach * reach) continue;
    const lo = qInvRot(b.q, [ox - b.c[0], oy - b.c[1], oz - b.c[2]]);
    const ld = qInvRot(b.q, [dx, dy, dz]);
    let tMin = 0, tMax = maxDist;
    let miss = false;
    for (let i = 0; i < 3; i++) {
      const h = b.h[i] + pad;
      if (Math.abs(ld[i]) < 1e-9) {
        if (Math.abs(lo[i]) > h) { miss = true; break; }
        continue;
      }
      let t1 = (-h - lo[i]) / ld[i];
      let t2 = (h - lo[i]) / ld[i];
      if (t1 > t2) { const tt = t1; t1 = t2; t2 = tt; }
      if (t1 > tMin) tMin = t1;
      if (t2 < tMax) tMax = t2;
      if (tMin > tMax) { miss = true; break; }
    }
    if (miss) continue;
    if (best === null || tMin < best.dist) best = { pid: b.pid, dist: tMin };
  }
  return best;
}

// ── lateral: sphere-vs-OBB push-out (corrected-position contract) ────────────

/**
 * Push a body sphere (belly height above the feet point, `radius` wide) out of
 * every piece box it penetrates; vertical contacts are skipped (stand/gravity
 * territory). Writes the corrected FEET position into `out`; true if moved.
 */
export function resolveObbs(obbs: readonly WorldObb[], px: number, py: number, pz: number,
  radius: number, out: { x: number; y: number; z: number }): boolean {
  const pr = Math.sqrt(px * px + py * py + pz * pz) || 1;
  const up: Vec3 = [px / pr, py / pr, pz / pr];
  let bx = px + up[0] * BODY_LIFT, by = py + up[1] * BODY_LIFT, bz = pz + up[2] * BODY_LIFT;
  let moved = false;
  for (let pass = 0; pass < 3; pass++) {
    let any = false;
    for (const b of obbs) {
      const dx = b.c[0] - bx, dy = b.c[1] - by, dz = b.c[2] - bz;
      const reach = b.r + radius;
      if (dx * dx + dy * dy + dz * dz > reach * reach) continue;
      const lp = qInvRot(b.q, [bx - b.c[0], by - b.c[1], bz - b.c[2]]);
      const clx = Math.max(-b.h[0], Math.min(b.h[0], lp[0]));
      const cly = Math.max(-b.h[1], Math.min(b.h[1], lp[1]));
      const clz = Math.max(-b.h[2], Math.min(b.h[2], lp[2]));
      let nx: number, ny: number, nz: number, dist: number;
      if (clx === lp[0] && cly === lp[1] && clz === lp[2]) {
        // Centre INSIDE the box: exit along the least-penetration axis.
        const pens = [b.h[0] - Math.abs(lp[0]), b.h[1] - Math.abs(lp[1]), b.h[2] - Math.abs(lp[2])];
        let ax = 0;
        if (pens[1] < pens[0]) ax = 1;
        if (pens[2] < pens[ax]) ax = 2;
        const ln: Vec3 = [0, 0, 0];
        ln[ax] = lp[ax] >= 0 ? 1 : -1;
        const wn = qRot(b.q, ln);
        nx = wn[0]; ny = wn[1]; nz = wn[2];
        dist = -(pens[ax]); // negative = penetration depth beyond the surface
      } else {
        const gx = lp[0] - clx, gy = lp[1] - cly, gz = lp[2] - clz;
        const g = Math.sqrt(gx * gx + gy * gy + gz * gz);
        if (g >= radius) continue;
        const wn = qRot(b.q, [gx / (g || 1), gy / (g || 1), gz / (g || 1)]);
        nx = wn[0]; ny = wn[1]; nz = wn[2];
        dist = g;
      }
      // Vertical contact (floor under / ceiling over the belly) → not ours.
      const vert = nx * up[0] + ny * up[1] + nz * up[2];
      if (Math.abs(vert) > VERTICAL_COS) continue;
      const push = radius - dist;
      bx += nx * push; by += ny * push; bz += nz * push;
      any = true; moved = true;
    }
    if (!any) break;
  }
  // CEILING pass: a head sphere accepting only downward pushes — jumping up
  // into a floor slab from below stops at the underside instead of tunnelling.
  let hx = bx + up[0] * (HEAD_LIFT - BODY_LIFT);
  let hy = by + up[1] * (HEAD_LIFT - BODY_LIFT);
  let hz = bz + up[2] * (HEAD_LIFT - BODY_LIFT);
  for (const b of obbs) {
    const dx = b.c[0] - hx, dy = b.c[1] - hy, dz = b.c[2] - hz;
    const reach = b.r + HEAD_RADIUS;
    if (dx * dx + dy * dy + dz * dz > reach * reach) continue;
    const lp = qInvRot(b.q, [hx - b.c[0], hy - b.c[1], hz - b.c[2]]);
    const clx = Math.max(-b.h[0], Math.min(b.h[0], lp[0]));
    const cly = Math.max(-b.h[1], Math.min(b.h[1], lp[1]));
    const clz = Math.max(-b.h[2], Math.min(b.h[2], lp[2]));
    let nx: number, ny: number, nz: number, push: number;
    if (clx === lp[0] && cly === lp[1] && clz === lp[2]) {
      const pens = [b.h[0] - Math.abs(lp[0]), b.h[1] - Math.abs(lp[1]), b.h[2] - Math.abs(lp[2])];
      let ax = 0;
      if (pens[1] < pens[0]) ax = 1;
      if (pens[2] < pens[ax]) ax = 2;
      const ln: Vec3 = [0, 0, 0];
      ln[ax] = lp[ax] >= 0 ? 1 : -1;
      const wn = qRot(b.q, ln);
      nx = wn[0]; ny = wn[1]; nz = wn[2];
      push = HEAD_RADIUS + pens[ax];
    } else {
      const gx = lp[0] - clx, gy = lp[1] - cly, gz = lp[2] - clz;
      const g = Math.sqrt(gx * gx + gy * gy + gz * gz);
      if (g >= HEAD_RADIUS) continue;
      const wn = qRot(b.q, [gx / (g || 1), gy / (g || 1), gz / (g || 1)]);
      nx = wn[0]; ny = wn[1]; nz = wn[2];
      push = HEAD_RADIUS - g;
    }
    if (nx * up[0] + ny * up[1] + nz * up[2] > -0.5) continue; // downward only
    hx += nx * push; hy += ny * push; hz += nz * push;
    bx += nx * push; by += ny * push; bz += nz * push;
    moved = true;
  }
  if (moved) {
    out.x = bx - up[0] * BODY_LIFT;
    out.y = by - up[1] * BODY_LIFT;
    out.z = bz - up[2] * BODY_LIFT;
  }
  return moved;
}
